import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useColorScheme, ColorSchemeName } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { lightTheme, darkTheme } from '../constants/theme';

// Definición de tipos
type ThemeType = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: typeof lightTheme | typeof darkTheme;
  themeMode: ThemeType;
  toggleTheme: () => void;
  setThemeMode: (mode: ThemeType) => void;
  isDarkMode: boolean;
}

interface ThemeProviderProps {
  children: ReactNode;
}

// Crear contexto
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// Clave para almacenar la preferencia de tema
const THEME_PREFERENCE_KEY = '@migym_theme_preference';

// Provider
export const ThemeProvider = ({ children }: ThemeProviderProps) => {
  // Obtener el esquema de colores del sistema
  const colorScheme = useColorScheme();
  
  // Estado para el modo de tema seleccionado
  const [themeMode, setThemeMode] = useState<ThemeType>('system');
  
  // Calculamos si estamos en modo oscuro basado en el modo de tema y esquema del sistema
  const isDarkMode = themeMode === 'system' 
    ? colorScheme === 'dark' 
    : themeMode === 'dark';
  
  // Tema actual basado en si estamos en modo oscuro
  const theme = isDarkMode ? darkTheme : lightTheme;
  
  // Alternar entre temas
  const toggleTheme = () => {
    const newMode = isDarkMode ? 'light' : 'dark';
    setThemeMode(newMode);
    saveThemePreference(newMode);
  };
  
  // Cambiar específicamente a un modo
  const setThemeModeAndSave = (mode: ThemeType) => {
    setThemeMode(mode);
    saveThemePreference(mode);
  };
  
  // Guardar preferencia en AsyncStorage
  const saveThemePreference = async (mode: ThemeType) => {
    try {
      await AsyncStorage.setItem(THEME_PREFERENCE_KEY, mode);
    } catch (error) {
      console.error('Error al guardar la preferencia de tema:', error);
    }
  };
  
  // Cargar preferencia al inicio
  useEffect(() => {
    const loadThemePreference = async () => {
      try {
        const savedTheme = await AsyncStorage.getItem(THEME_PREFERENCE_KEY);
        if (savedTheme) {
          setThemeMode(savedTheme as ThemeType);
        }
      } catch (error) {
        console.error('Error al cargar la preferencia de tema:', error);
      }
    };
    
    loadThemePreference();
  }, []);
  
  // Provide the theme context to all children
  return (
    <ThemeContext.Provider
      value={{
        theme,
        themeMode,
        toggleTheme,
        setThemeMode: setThemeModeAndSave,
        isDarkMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

// Hook para usar el tema
export const useTheme = () => {
  const context = useContext(ThemeContext);
  
  if (context === undefined) {
    throw new Error('useTheme debe ser usado dentro de un ThemeProvider');
  }
  
  return context;
}; 